/** Patient-portal / public-booking display helpers matching the approved mockups. */

import {
  formatCourseClock,
  formatCourseDateTime,
  formatCourseDateTimeRange,
  formatCourseTime,
  parseCourseCivil,
  parseCourseInstant,
} from '@/lib/courseDateFormat';
import { CLINIC_TIMEZONE } from '@/lib/reporting/timezone';

export function formatPortalKroner(cents: number | null | undefined): string {
  const value = Math.round((cents ?? 0) / 100);
  return `${value.toLocaleString('da-DK')} kr.`;
}

export function formatPortalClock(iso: string | null | undefined): string {
  return formatCourseTime(iso);
}

export function formatPortalDateTime(iso: string | null | undefined): string {
  return formatCourseDateTime(iso, 'da');
}

export function formatPortalDateTimeRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string {
  return formatCourseDateTimeRange(startIso, endIso, 'da');
}

export function clinicCivilDate(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CLINIC_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function formatPortalLongDate(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('da-DK', {
    timeZone: CLINIC_TIMEZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).formatToParts(now);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const titled = weekday ? weekday.charAt(0).toUpperCase() + weekday.slice(1) : '';
  return `${titled} ${day}. ${month} ${year}`.replace(/\s+/g, ' ').trim();
}

export function formatRelativeDays(from: Date, toIso: string): string {
  const start = clinicCivilDate(from);
  const end = clinicCivilDate(toIso);
  const startMs = new Date(`${start}T12:00:00`).getTime();
  const endMs = new Date(`${end}T12:00:00`).getTime();
  const days = Math.round((endMs - startMs) / 86_400_000);
  if (days <= 0) return 'i dag';
  if (days === 1) return 'i morgen';
  return `om ${days} dage`;
}

export function formatPortalMonthShort(iso: string): string {
  const parts = parseCourseInstant(iso);
  if (!parts) return '';
  return [
    'jan.',
    'feb.',
    'mar.',
    'apr.',
    'maj',
    'jun.',
    'jul.',
    'aug.',
    'sep.',
    'okt.',
    'nov.',
    'dec.',
  ][parts.month];
}

export function formatPortalDayHeading(civil: string): string {
  const parts = parseCourseCivil(civil);
  if (!parts) return civil;
  const weekday = ['søn.', 'man.', 'tirs.', 'ons.', 'tors.', 'fre.', 'lør.'][parts.weekday];
  return `${weekday} ${parts.day}. ${formatPortalMonthShort(parts.date.toISOString())}`;
}

export function formatPortalWeekRange(weekStartCivil: string, weekEndCivil: string): string {
  const start = parseCourseCivil(weekStartCivil);
  const end = parseCourseCivil(weekEndCivil);
  if (!start || !end) return '';
  return `${start.day}.–${end.day}. ${formatPortalMonthShort(end.date.toISOString())} ${end.year}`;
}

export function isoWeekNumber(civil: string): number {
  const date = new Date(`${civil}T12:00:00`);
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return Math.ceil(((utc.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

export function mondayOf(civil: string): string {
  const date = new Date(`${civil}T12:00:00`);
  const day = date.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + offset);
  return clinicCivilDate(date);
}

/** First Monday that contains a bookable civil day; never before this week's Monday. */
export function firstBookableWeekMonday(
  bookableCivils: readonly string[],
  todayMonday: string,
): string {
  const first = [...bookableCivils]
    .filter((civil) => civil >= todayMonday)
    .sort()
    .find((civil) => mondayOf(civil) >= todayMonday);
  return first ? mondayOf(first) : todayMonday;
}

export function addCivilDays(civil: string, days: number): string {
  const date = new Date(`${civil}T12:00:00`);
  date.setDate(date.getDate() + days);
  return clinicCivilDate(date);
}

export function weekdaysMondayToFriday(weekMonday: string): string[] {
  return [0, 1, 2, 3, 4].map((offset) => addCivilDays(weekMonday, offset));
}

export function formatClinicAddress(address: unknown): string {
  if (!address || typeof address !== 'object') return '';
  const row = address as { street?: unknown; zip?: unknown; city?: unknown };
  const street = typeof row.street === 'string' ? row.street.trim() : '';
  const zip = typeof row.zip === 'string' ? row.zip.trim() : '';
  const city = typeof row.city === 'string' ? row.city.trim() : '';
  const place = [zip, city].filter(Boolean).join(' ');
  return [street, place].filter(Boolean).join(', ');
}

export function formatDeadlineUntil(iso: string): string {
  return formatCourseDateTime(iso, 'da');
}

export function clockFromIso(iso: string): string {
  const parts = parseCourseInstant(iso);
  if (!parts) return '';
  return formatCourseClock(parts.hours, parts.minutes);
}

export function formatPortalShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const parts = parseCourseInstant(iso);
  if (!parts) return '';
  return `${parts.day}. ${formatPortalMonthShort(iso)} ${parts.year}`;
}

export function formatPortalFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  const mb = bytes / (1024 * 1024);
  return `${mb.toLocaleString('da-DK', { maximumFractionDigits: 1 })} MB`;
}

/** Avoid “Konsultation 30 min · 30 min” when the service name already has the duration. */
export function bookingServiceSummaryLabel(
  name: string | null | undefined,
  durationMinutes: number | null | undefined,
): string {
  const title = name?.trim() || 'Behandling';
  if (durationMinutes == null || Number.isNaN(durationMinutes)) return title;
  const already = new RegExp(`\\b${durationMinutes}\\s*min\\b`, 'i').test(title);
  return already ? title : `${title} · ${durationMinutes} min`;
}

export function coursePortalStartPhrase(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const word = at.getTime() > now.getTime() ? 'starter' : 'startet';
  return ` · ${word} ${formatPortalDateTime(iso)}`;
}

export function bookingCancellationHint(input: {
  deadlineHours: number;
  reschedulingAllowed: boolean;
}): string {
  const hours = Number.isFinite(input.deadlineHours) ? input.deadlineHours : 24;
  const action = input.reschedulingAllowed ? 'flytte eller aflyse' : 'aflyse';
  return `Du kan ${action} uden gebyr indtil ${hours} timer før.`;
}

export function greetingDa(period: 'morning' | 'forenoon' | 'afternoon' | 'evening', name?: string | null): string {
  const base =
    period === 'morning'
      ? 'Godmorgen'
      : period === 'forenoon'
        ? 'God formiddag'
        : period === 'afternoon'
          ? 'God eftermiddag'
          : 'God aften';
  const trimmed = name?.trim();
  return trimmed ? `${base}, ${trimmed}` : base;
}
